import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import './Planner.css';

interface OrderLine {
  id: number;
  product: number;
  product_name: string;
  calculated_quantity_bags: number;
  customer_adjusted_quantity_bags: number | null;
  final_quantity_bags: number;
}

interface PlannedOrder {
  id: string;
  plan_name: string;
  customer_name: string;
  estimated_ship_date: string;
  verification_deadline: string;
  status: string;
  delivery_method: string;
  lines: OrderLine[];
}

const OrderVerification: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<PlannedOrder | null>(null);
  const [adjustments, setAdjustments] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [token]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/planner/public/verify/${token}/`);
      setOrder(response.data);

      // Initialize adjustments with current quantities
      const initial: Record<number, number> = {};
      response.data.lines.forEach((line: OrderLine) => {
        initial[line.id] = line.customer_adjusted_quantity_bags || line.calculated_quantity_bags;
      });
      setAdjustments(initial);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (lineId: number, value: string) => {
    const qty = parseInt(value) || 0;
    setAdjustments(prev => ({ ...prev, [lineId]: qty }));
  };

  const hasChanges = () => {
    if (!order) return false;
    return order.lines.some(line => {
      const original = line.calculated_quantity_bags;
      const adjusted = adjustments[line.id] || original;
      return adjusted !== original;
    });
  };

  const handleApprove = async () => {
    if (!order) return;
    setSubmitting(true);

    try {
      const lines = order.lines.map(line => ({
        line_id: line.id,
        adjusted_quantity_bags: adjustments[line.id] || line.calculated_quantity_bags
      }));

      await api.post(`/planner/public/verify/${token}/`, {
        token,
        approved: true,
        lines,
        notes
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!order) return;
    setSubmitting(true);

    try {
      const lines = order.lines.map(line => ({
        line_id: line.id,
        adjusted_quantity_bags: adjustments[line.id] || line.calculated_quantity_bags,
        reason: 'Customer adjustment'
      }));

      await api.post(`/planner/public/verify/${token}/`, {
        token,
        approved: false,
        lines,
        notes
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit changes');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="planner-loading">Loading order details...</div>;
  }

  if (error) {
    return (
      <div className="verification-page">
        <div className="verification-card">
          <div className="planner-error">{error}</div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="verification-page">
        <div className="verification-card">
          <div className="success-message">
            <h2>Thank You!</h2>
            <p>Your order verification has been submitted successfully.</p>
            {hasChanges() ? (
              <p>We'll review your changes and reach out if we have any questions.</p>
            ) : (
              <p>Your order will be processed for the scheduled ship date.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  const totalBags = Object.values(adjustments).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="verification-page">
      <div className="verification-card">
        <div className="verification-header">
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." style={{ height: '60px', marginBottom: '16px' }} />
          <h1>Order Verification</h1>
          <p>Please review and confirm your upcoming feed order</p>

          <div className="order-meta">
            <span><strong>Ship Date:</strong> {order.estimated_ship_date}</span>
            <span><strong>Deadline:</strong> {order.verification_deadline}</span>
            <span><strong>Delivery:</strong> {order.delivery_method}</span>
          </div>
        </div>

        <div className="order-lines">
          <h3>Order Items</h3>
          <p>Review quantities and make any adjustments needed:</p>

          {order.lines.map(line => (
            <div key={line.id} className="order-line">
              <div className="line-product">
                <span>{line.product_name}</span>
                <span className="suggested-qty">
                  (Suggested: {line.calculated_quantity_bags} bags)
                </span>
              </div>
              <div className="line-quantity">
                <input
                  type="text"
                  inputMode="numeric"
                  className="quantity-input"
                  value={adjustments[line.id] || ''}
                  onChange={(e) => handleQuantityChange(line.id, e.target.value)}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 0) {
                      handleQuantityChange(line.id, String(val));
                    }
                  }}
                />
                <span>bags</span>
              </div>
            </div>
          ))}

          <div className="order-total">
            <strong>Total: {totalBags} bags</strong>
          </div>
        </div>

        <div className="verification-actions">
          <textarea
            className="notes-input"
            placeholder="Any special instructions or notes? (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <div className="btn-group">
            {hasChanges() ? (
              <button
                className="btn-save-changes"
                onClick={handleSaveChanges}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Changes for Review'}
              </button>
            ) : (
              <button
                className="btn-approve"
                onClick={handleApprove}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Approve Order'}
              </button>
            )}
          </div>

          <p className="verification-note">
            {hasChanges()
              ? 'You\'ve made changes. Our team will review and confirm.'
              : 'Approving will confirm this order for processing.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderVerification;
